// Contexto da FAIXA UNICA de alertas (2026-08-03).
//
// Antes: 13 componentes independentes empilhavam ate 14 faixas de 35px no topo de TODA tela —
// 140px comidos num dia comum, e o conteudo da tela empurrado pra baixo. Cada faixa continua
// dona do seu dado, do seu texto e do seu "X"; o que mudou e QUEM decide se ela aparece.
//
// Como funciona: cada banner chama `useAlertaFaixa(id, dados | null)` ANTES dos seus early
// returns (regra dos hooks) e recebe de volta `recolhido`. Se recolhido, ele devolve null e
// quem desenha e a <BarraAlertasResumo />, que le o registro e mostra uma pastilha por alerta.
//
// Duas decisoes que valem registro:
//  - Com UM alerta so a barra nao aparece: colapsar uma faixa unica esconderia o valor e o
//    botao de acao sem ganhar espaco nenhum. O problema que a barra resolve e o EMPILHAMENTO.
//  - A preferencia (recolhido/aberto) e persistente, nao por dia. Ela e visualizacao, nao
//    dispensa — os "X" continuam sendo o mecanismo de dispensar, cada um com sua regra.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface AlertaRegistro {
  /** Rotulo curto da pastilha (ex.: "Contas a pagar"). */
  rotulo: string;
  /** Fundo solido da faixa — vira a cor da pastilha. */
  bg: string;
  /** Borda esquerda da faixa — vira a borda da pastilha. */
  borda: string;
  /** Itens por tras do alerta. Aparece na pastilha so quando > 1. */
  qtd: number;
}

// Ordem canonica: a MESMA em que as faixas aparecem no Layout. As pastilhas seguem ela pra
// que a leitura da barra bata com a leitura da lista expandida.
export const ORDEM_ALERTAS = [
  'saude-banco',
  'notas-duplicadas',
  'notas-problemas',
  'buracos-numeracao',
  'contas-pagar',
  'boletos-vencidos',
  'conta-negativa',
  'caixa-divergencia',
  'movimento-a-fechar',
  'transferencias',
  'trocas',
  'entrega-whatsapp',
  'parados',
  'perdidos',
  'esquecidos',
  'conciliacao',
  'estoque-divergencia',
  'estoque-negativo',
  'estoque-texto-livre',
  'estoque-sem-estrela',
  'estoque-sem-endereco',
  'ean-invalido',
  'divergencia-recebimento',
  'reposicao',
  'retomar-orcamentos',
] as const;

// Gravidade por cor de fundo — decide qual borda a barra recolhida usa. Segue a escala que as
// faixas ja praticavam: vermelho (para a loja) > ambar escuro > laranja > ambar > ouro > creme.
const PESO_GRAVIDADE: Record<string, number> = {
  '#ef4444': 5,
  '#b45309': 4,
  '#f97316': 3,
  '#f59e0b': 2,
  '#eab308': 1,
  '#fde68a': 0,
};

const KEY_RECOLHIDO = 'alertas.faixa.recolhido';

function lerPreferencia(): boolean {
  // Padrao = recolhido: o ganho de espaco e o motivo da mudanca.
  return localStorage.getItem(KEY_RECOLHIDO) !== '0';
}

interface Ctx {
  registro: Record<string, AlertaRegistro>;
  registrar: (id: string, dados: AlertaRegistro | null) => void;
  /** Um banner deve se esconder? So true quando ha 2+ alertas E a preferencia e recolhida. */
  recolhido: boolean;
  /** A preferencia crua, sem o piso de 2 alertas (a barra usa pra decidir a seta). */
  preferenciaRecolhida: boolean;
  alternar: () => void;
  /** Alertas ativos na ordem canonica. */
  ativos: Array<AlertaRegistro & { id: string }>;
}

const AlertasCtx = createContext<Ctx | null>(null);

export function AlertasProvider({ children }: { children: ReactNode }) {
  const [registro, setRegistro] = useState<Record<string, AlertaRegistro>>({});
  const [preferenciaRecolhida, setPreferencia] = useState(lerPreferencia);

  // Estavel de proposito: o hook de cada banner depende dela, e uma identidade nova a cada
  // render faria o useEffect do registro rodar em loop.
  const registrar = useCallback((id: string, dados: AlertaRegistro | null) => {
    setRegistro((prev) => {
      const atual = prev[id];
      if (!dados) {
        if (!atual) return prev;
        const copia = { ...prev };
        delete copia[id];
        return copia;
      }
      if (atual && atual.rotulo === dados.rotulo && atual.bg === dados.bg
        && atual.borda === dados.borda && atual.qtd === dados.qtd) return prev;
      return { ...prev, [id]: dados };
    });
  }, []);

  const ativos = useMemo(() => {
    const lista: Array<AlertaRegistro & { id: string }> = [];
    for (const id of ORDEM_ALERTAS) {
      const r = registro[id];
      if (r) lista.push({ id, ...r });
    }
    return lista;
  }, [registro]);

  const alternar = useCallback(() => {
    setPreferencia((r) => {
      localStorage.setItem(KEY_RECOLHIDO, r ? '0' : '1');
      return !r;
    });
  }, []);

  const valor = useMemo<Ctx>(() => ({
    registro,
    registrar,
    recolhido: preferenciaRecolhida && ativos.length >= 2,
    preferenciaRecolhida,
    alternar,
    ativos,
  }), [registro, registrar, preferenciaRecolhida, alternar, ativos]);

  return <AlertasCtx.Provider value={valor}>{children}</AlertasCtx.Provider>;
}

export function useAlertasCtx(): Ctx | null {
  return useContext(AlertasCtx);
}

/**
 * Registra a faixa e devolve se ela deve se esconder atras da barra resumida.
 *
 * Chamar SEMPRE no topo do componente, antes de qualquer `return` condicional — passar `null`
 * quando o alerta nao esta ativo (sem permissao, sem dado, dispensado, contagem zero).
 */
export function useAlertaFaixa(id: string, dados: AlertaRegistro | null): boolean {
  const ctx = useAlertasCtx();
  const registrar = ctx?.registrar;

  // Deps primitivas: o objeto `dados` e novo a cada render e sozinho dispararia o efeito sempre.
  const rotulo = dados?.rotulo ?? null;
  const bg = dados?.bg ?? null;
  const borda = dados?.borda ?? null;
  const qtd = dados?.qtd ?? 0;

  useEffect(() => {
    if (!registrar) return;
    registrar(id, rotulo && bg && borda ? { rotulo, bg, borda, qtd } : null);
  }, [registrar, id, rotulo, bg, borda, qtd]);

  // Desmontou (trocou de perfil, deslogou): sai do registro pra nao deixar pastilha fantasma.
  useEffect(() => () => registrar?.(id, null), [registrar, id]);

  return ctx?.recolhido ?? false;
}

export function corMaisGrave(ativos: AlertaRegistro[]): { bg: string; borda: string } {
  let melhor: AlertaRegistro | null = null;
  for (const a of ativos) {
    if (!melhor || (PESO_GRAVIDADE[a.bg] ?? 0) > (PESO_GRAVIDADE[melhor.bg] ?? 0)) melhor = a;
  }
  // Sem alerta nenhum a barra nem renderiza; o fallback so existe pra fechar o tipo.
  return melhor ? { bg: melhor.bg, borda: melhor.borda } : { bg: '#cbd5e1', borda: '#475569' };
}
